import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, Button, Field, apiErrorMessage, brandDisplayName, brandLocalDate, rebaseDatePrefill, useBrand } from '@aralere/shared-ui';
import type { Business, LicenseExpensePrompt, RecurringFrequency, Vendor } from '@aralere/shared-types';

// The PRE-FILLED linked license-expense create (Stage-3 slice 2; s43 record §3 — the double-count
// guard's "prompted, NEVER a silent auto-create"). Opened by (a) a house-client self-assign whose
// response carried `licenseExpensePrompt` (nothing currently links the service), and (b) the
// Services editor's "License & seats" panel when a service has no linked license. Prefills: type
// license, the linked service, businessId = the service's defaultBillingEntity, price per seat =
// the listed defaultCostToYou (manual services included, same listed-cost prefill) — amount and
// charge day get the owner's eyes here before anything is created. Renders via
// createPortal(document.body) per the locked portal-modal rule (02, s24).

const FREQ_LABEL: Record<RecurringFrequency, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
  custom: 'Every N months',
};

export function CreateLicenseExpenseDialog({
  prompt,
  businesses,
  onCreated,
  onClose,
}: {
  /** Null = closed. */
  prompt: LicenseExpensePrompt | null;
  businesses: Business[];
  onCreated: () => void;
  onClose: () => void;
}): JSX.Element | null {
  const { brand: portalBrand } = useBrand();
  /** 04 #136: a business's timezone (its `timezone`, absent = the platform default); with no
   *  business chosen, the portal brand's. The start date is taken on that brand's clock. */
  const bizTz = (id: string): string | null => {
    const b = businesses.find((x) => x.id === id);
    return b ? b.timezone ?? null : portalBrand?.timezone ?? null;
  };
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [description, setDescription] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [pricePerSeat, setPricePerSeat] = useState('');
  const [seatsPerBlock, setSeatsPerBlock] = useState('1');
  const [seatsFloor, setSeatsFloor] = useState('');
  const [externalSeats, setExternalSeats] = useState('');
  const [frequency, setFrequency] = useState<RecurringFrequency>('monthly');
  const [frequencyMonths, setFrequencyMonths] = useState('');
  const [chargeDay, setChargeDay] = useState('1');
  const [startDate, setStartDate] = useState(() => brandLocalDate(portalBrand?.timezone ?? null));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!prompt) return;
    setDescription(`${prompt.serviceName} seats`);
    setBusinessId(prompt.businessId);
    setVendorId('');
    setPricePerSeat(prompt.suggestedPricePerSeat != null ? String(prompt.suggestedPricePerSeat) : '');
    setSeatsPerBlock('1');
    setSeatsFloor('');
    setExternalSeats('');
    setFrequency('monthly');
    setFrequencyMonths('');
    setChargeDay('1');
    setStartDate(brandLocalDate(bizTz(prompt.businessId)));
    setErr(null);
    void (async () => {
      try {
        const res = await api.admin.listVendors();
        setVendors(res.vendors.filter((v) => v.active));
      } catch {
        /* vendor list is a convenience — the field stays optional */
      }
    })();
  }, [prompt]);

  if (!prompt) return null;

  const price = Number(pricePerSeat.trim());
  const block = Number(seatsPerBlock.trim());
  const floor = seatsFloor.trim() === '' ? 0 : Number(seatsFloor.trim());
  const external = externalSeats.trim() === '' ? 0 : Number(externalSeats.trim());
  const ceilToBlock = (n: number, b: number): number => Math.ceil(Math.max(0, n) / Math.max(1, b)) * Math.max(1, b);
  const inUse = prompt.seatsInUse + (Number.isFinite(external) ? external : 0);
  const preview =
    Number.isFinite(price) && price >= 0 && Number.isInteger(block) && block >= 1
      ? (() => {
          const purchased = Math.max(ceilToBlock(inUse, block), ceilToBlock(Number.isFinite(floor) ? floor : 0, block));
          return { purchased, amount: Math.round(purchased * price * 100) / 100 };
        })()
      : null;

  const submit = async (): Promise<void> => {
    setErr(null);
    if (businessId === '') { setErr('Pick the business this expense belongs to.'); return; }
    if (description.trim() === '') { setErr('A description is required.'); return; }
    if (!(price >= 0) || !Number.isFinite(price)) { setErr('Price per seat must be a number ≥ 0.'); return; }
    if (!Number.isInteger(block) || block < 1) { setErr('Seats per block must be a whole number ≥ 1.'); return; }
    const cd = Number(chargeDay.trim());
    if (!Number.isInteger(cd) || cd < 1 || cd > 31) { setErr('Charge day must be a whole number from 1 to 31.'); return; }
    let fm: number | null = null;
    if (frequency === 'custom') {
      fm = Number(frequencyMonths.trim());
      if (!Number.isInteger(fm) || fm < 1 || fm > 120) { setErr('Every-N-months must be a whole number from 1 to 120.'); return; }
    }
    setBusy(true);
    try {
      await api.admin.createRecurringExpense({
        businessId,
        type: 'license',
        description: description.trim(),
        linkedServiceId: prompt.serviceId,
        linkedVendorId: vendorId || null,
        pricePerSeat: price,
        seatsPerBlock: block,
        seatsFloor: seatsFloor.trim() === '' ? null : floor,
        externalSeats: externalSeats.trim() === '' ? null : external,
        frequency,
        frequencyMonths: fm,
        chargeDay: cd,
        startDate,
      });
      onCreated();
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] p-5 shadow-xl space-y-4 max-h-[90vh] overflow-y-auto">
        <div>
          <p className="text-lg font-medium">Create linked license expense</p>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Nothing currently links <strong>{prompt.serviceName}</strong> on the cost side. This
            records what <em>you</em> pay the vendor
            {prompt.vendorName ? <> ({prompt.vendorName})</> : null}. The assignment itself never
            books cost, so without this the seats won't count in profitability.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Description" required>
            <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Field label="Business" required>
            <select
              className={inputCls}
              value={businessId}
              onChange={(e) => {
                const next = e.target.value;
                // 04 #136: an untouched start-date prefill follows the newly chosen brand's clock.
                setStartDate((d) => rebaseDatePrefill(d, bizTz(businessId), bizTz(next)));
                setBusinessId(next);
              }}
            >
              <option value="">Select…</option>
              {businesses.map((b) => (
                <option key={b.id} value={b.id}>{brandDisplayName(b.name)}</option>
              ))}
            </select>
          </Field>
          <Field label="Vendor (optional)">
            <select className={inputCls} value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">None</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>{v.displayName}</option>
              ))}
            </select>
          </Field>
          <Field
            label="Price per seat"
            required
            tip="What one seat costs you per cycle. The expense amount derives from this automatically: seats in use, rounded up to your purchase block, times this price. Prefilled from the service's Cost field."
          >
            <input className={inputCls} value={pricePerSeat} onChange={(e) => setPricePerSeat(e.target.value)} placeholder="16.40" />
          </Field>
          <Field
            label="Seats per block"
            required
            tip="How the vendor sells seats. 1 means you buy them one at a time (per-seat, like CSP). 10 means seats come in packs of ten, so going from 10 to 11 in use means buying another block of 10."
          >
            <input className={inputCls} value={seatsPerBlock} onChange={(e) => setSeatsPerBlock(e.target.value)} placeholder="1" />
          </Field>
          <Field
            label="Prepaid floor (seats, optional)"
            tip="A minimum you're committed to paying for even when unused, in seats. Leave blank for none."
          >
            <input className={inputCls} value={seatsFloor} onChange={(e) => setSeatsFloor(e.target.value)} placeholder="none" />
          </Field>
          <Field
            label="Extra seats NOT assigned in the portal (optional)"
            tip="Leave this 0 in almost every case. It is ONLY for seats you pay for that are not assigned to any client in the portal (e.g. deployed by hand before you used the portal). Portal assignments are counted automatically; do not re-enter them here or they'll be counted twice."
          >
            <input className={inputCls} value={externalSeats} onChange={(e) => setExternalSeats(e.target.value)} placeholder="0" />
          </Field>
          <Field label="Frequency" required>
            <select className={inputCls} value={frequency} onChange={(e) => setFrequency(e.target.value as RecurringFrequency)}>
              {(Object.keys(FREQ_LABEL) as RecurringFrequency[]).map((fq) => (
                <option key={fq} value={fq}>{FREQ_LABEL[fq]}</option>
              ))}
            </select>
          </Field>
          {frequency === 'custom' ? (
            <Field label="Every N months" required>
              <input className={inputCls} value={frequencyMonths} onChange={(e) => setFrequencyMonths(e.target.value)} placeholder="6" />
            </Field>
          ) : null}
          <Field label="Charge day (of month)" required tip="The day of the month the money actually leaves your account.">
            <input className={inputCls} value={chargeDay} onChange={(e) => setChargeDay(e.target.value)} placeholder="1" />
          </Field>
          <Field label="Start date" required>
            <input type="date" className={inputCls} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
        </div>

        <p className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] px-3 py-2 text-sm">
          {preview ? (
            <>
              Starting from <strong>{inUse}</strong> seat{inUse === 1 ? '' : 's'}
              {external > 0 ? <> ({external} outside the portal)</> : null}, the expense derives{' '}
              <strong>{preview.purchased} seat{preview.purchased === 1 ? '' : 's'} purchased</strong> at{' '}
              <strong>${preview.amount.toFixed(2)}</strong> per cycle. Any seats already assigned to
              clients in the portal are added automatically when you save, and it tracks assignments
              from there.
            </>
          ) : (
            <>Enter a price per seat and block size to preview the derived cost.</>
          )}
        </p>

        {err ? <p className="text-sm text-[var(--tint-red-text)]">{err}</p> : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Not now
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Creating…' : 'Create expense'}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

